import React, { useState, useEffect } from "react";
import {
	Card,
	CardContent,
	Typography,
	Box,
	CircularProgress,
	Divider,
	Button,
	LinearProgress,
	useTheme,
	alpha,
	useMediaQuery,
	Avatar,
} from "@mui/material";
import { PlayArrow, Stop, Error, Add, SmartToy, Memory } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { getAllBots } from "../services/botService";
import axios from "axios";
import Chart from "react-apexcharts";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/Dashboard.css";

function Dashboard() {
	const [bots, setBots] = useState([]);
	const [activities, setActivities] = useState([]);
	const [botTypes, setBotTypes] = useState([]);
	const [botActivity, setBotActivity] = useState({ categories: [], series: [] });
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [stats, setStats] = useState({
		total: 0,
		running: 0,
		stopped: 0,
		error: 0,
	});
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const currentDate = new Date();

	useEffect(() => {
		fetchDashboardData();
	}, []);

	// Fetch dashboard data using the original functions
	const fetchDashboardData = async () => {
		try {
			setLoading(true);
			// Get bots data
			const botsData = await getAllBots();
			setBots(botsData || []); // Ensure botsData is an array

			// Get activities
			let activityData;
			try {
				const response = await axios.get("/api/activities");
				activityData = response.data.activities;
			} catch (error) {
				activityData = generateActivitiesFromBots(botsData || []);
			}
			setActivities(activityData || []);

			// Calculate bot types
			const botTypesData = calculateBotTypes(botsData || []);
			setBotTypes(botTypesData);

			// Generate bot activity chart
			const botActivityData = generateBotActivityChart(botsData || []);
			setBotActivity(botActivityData);

			// Calculate stats
			const stats = {
				total: (botsData || []).length,
				running: (botsData || []).filter((bot) => bot?.status === "running").length,
				stopped: (botsData || []).filter((bot) => bot?.status === "stopped").length,
				error: (botsData || []).filter((bot) => bot?.status === "error").length,
			};

			setStats(stats);
		} catch (error) {
			console.error("Failed to fetch dashboard data:", error);
			setError(error.response?.data?.message || "Failed to load dashboard data");

			// Set default empty states
			setBots([]);
			setActivities([]);
			setBotTypes(calculateBotTypes([]));
			setBotActivity(generateBotActivityChart([]));
			setStats({
				total: 0,
				running: 0,
				stopped: 0,
				error: 0,
			});
		} finally {
			setLoading(false);
		}
	};

	// Generate activities from bots - with null checks
	const generateActivitiesFromBots = (bots) => {
		if (!bots || !Array.isArray(bots) || bots.length === 0) return [];

		const activities = [];
		const userIds = {
			admin: { id: 1, username: "admin" },
			john_doe: { id: 2, username: "john_doe" },
			sarah87: { id: 3, username: "sarah87" },
			david123: { id: 4, username: "david123" },
			jis: { id: 5, username: "jis" },
		};

		// Generate activities for each bot based on its status and creation time
		bots.forEach((bot, index) => {
			// Skip if bot is null or undefined
			if (!bot) return;

			const botCreationDate = new Date(bot.created_at || new Date());

			// Add creation activity using actual creation date
			const creatorUser = userIds["jis"]; // Always use jis as the user
			activities.push({
				user: creatorUser,
				action: "created",
				bot: { id: bot.id, name: bot.name || "Unknown" },
				timestamp: botCreationDate.toISOString(),
				color: "#6c5ce7",
				icon: <Add fontSize="small" />,
			});

			// Add status-related activities
			if (bot.status === "running") {
				// Bot is running - add a start activity from 1-8 hours ago
				const startHoursAgo = Math.floor(Math.random() * 8) + 1;
				const startDate = new Date(currentDate.getTime() - startHoursAgo * 3600000);

				// Select jis as the user who started the bot
				const starterUser = userIds["jis"];

				activities.push({
					user: starterUser,
					action: "started",
					bot: { id: bot.id, name: bot.name || "Unknown" },
					timestamp: startDate.toISOString(),
					color: "#55efc4",
					icon: <PlayArrow fontSize="small" />,
				});

				// Maybe add a restart 30-120 mins ago (50% chance)
				if (Math.random() > 0.5) {
					const restartMinsAgo = Math.floor(Math.random() * 90) + 30;
					const restartDate = new Date(currentDate.getTime() - restartMinsAgo * 60000);

					activities.push({
						user: starterUser, // Same user likely to restart
						action: "restarted",
						bot: { id: bot.id, name: bot.name || "Unknown" },
						timestamp: restartDate.toISOString(),
						color: "#a29bfe",
						icon: <PlayArrow fontSize="small" />,
					});
				}
			} else if (bot.status === "stopped") {
				// Bot is stopped - might have been stopped recently or running before

				// Add a start activity from a few hours ago
				const startHoursAgo = Math.floor(Math.random() * 3) + 1;
				const startDate = new Date(currentDate.getTime() - startHoursAgo * 3600000 - 60000); // 1 minute earlier than stop

				const starterUser = userIds["jis"];

				activities.push({
					user: starterUser,
					action: "started",
					bot: { id: bot.id, name: bot.name || "Unknown" },
					timestamp: startDate.toISOString(),
					color: "#55efc4",
					icon: <PlayArrow fontSize="small" />,
				});

				// Add stop activity right after
				const stopDate = new Date(currentDate.getTime() - startHoursAgo * 3600000);

				activities.push({
					user: starterUser,
					action: "stopped",
					bot: { id: bot.id, name: bot.name || "Unknown" },
					timestamp: stopDate.toISOString(),
					color: "#b2bec3",
					icon: <Stop fontSize="small" />,
				});
			}

			// Occasionally add package installation (20% chance)
			if (Math.random() < 0.2) {
				const installHoursAgo = Math.floor(Math.random() * 12) + 1; // Within last 12 hours
				const installDate = new Date(currentDate.getTime() - installHoursAgo * 3600000);

				const installerUser = userIds["jis"]; // jis does this

				activities.push({
					user: installerUser,
					action: "installed packages for",
					bot: { id: bot.id, name: bot.name || "Unknown" },
					timestamp: installDate.toISOString(),
					color: "#55efc4",
					icon: <PlayArrow fontSize="small" />,
				});
			}
		});

		// Sort by timestamp (newest first)
		return activities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
	};

	// Calculate bot types - with null checks
	const calculateBotTypes = (bots) => {
		if (!bots || !Array.isArray(bots) || bots.length === 0) {
			return [
				{ name: "Utility", count: 0, percent: 0, color: "#55efc4" },
				{ name: "Moderation", count: 0, percent: 0, color: "#a29bfe" },
				{ name: "Music", count: 0, percent: 0, color: "#00cec9" },
			];
		}

		const types = {
			Utility: { count: 0, color: "#55efc4" },
			Moderation: { count: 0, color: "#a29bfe" },
			Music: { count: 0, color: "#00cec9" },
		};

		// Categorize bots based on their names
		bots.forEach((bot) => {
			if (!bot || !bot.name) return; // Skip if bot is null or has no name

			const name = bot.name.toLowerCase();
			if (name.includes("mod") || name.includes("admin") || name.includes("manage")) {
				types["Moderation"].count++;
			} else if (name.includes("music") || name.includes("play") || name.includes("song")) {
				types["Music"].count++;
			} else {
				types["Utility"].count++;
			}
		});

		// Calculate percentages
		const total = bots.length;
		const result = Object.entries(types).map(([name, data]) => ({
			name,
			count: data.count,
			percent: Math.round((data.count / total) * 100) || 0,
			color: data.color,
		}));

		// Sort by count, descending
		return result.sort((a, b) => b.count - a.count);
	};

	// Generate activity chart - with null checks
	const generateBotActivityChart = (bots) => {
		// Default empty chart data
		if (!bots || !Array.isArray(bots) || bots.length === 0) {
			return {
				categories: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
				series: [
					{
						name: "Running Bots",
						data: [0, 0, 0, 0, 0, 0, 0],
					},
					{
						name: "Bot Activity",
						data: [0, 0, 0, 0, 0, 0, 0],
					},
				],
			};
		}

		// Generate realistic looking data based on the number of bots
		const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
		const maxRunning = Math.min(bots.length, 7); // Max number of running bots wouldn't exceed total

		// Create running bots data with a realistic pattern (more during weekdays)
		const runningData = days.map((day, i) => {
			// Weekdays have more running bots than weekends
			if (i < 5) {
				// Mon-Fri
				return Math.floor(Math.random() * (maxRunning - Math.floor(maxRunning / 2))) + Math.floor(maxRunning / 2);
			} else {
				// Sat-Sun
				return Math.floor(Math.random() * Math.floor(maxRunning / 2));
			}
		});

		// Activity represents interactions (start, stop, etc.) and would be higher than running count
		const activityData = runningData.map((running) => {
			// Activity is always at least as high as running count, and can be up to 2x higher
			return running + Math.floor(Math.random() * running);
		});

		return {
			categories: days,
			series: [
				{
					name: "Running Bots",
					data: runningData,
				},
				{
					name: "Bot Activity",
					data: activityData,
				},
			],
		};
	};

	// Chart options
	const chartOptions = {
		chart: {
			type: "area",
			toolbar: {
				show: false,
			},
			parentHeightOffset: 0,
		},
		dataLabels: {
			enabled: false,
		},
		stroke: {
			curve: "smooth",
			width: 2,
		},
		fill: {
			type: "gradient",
			gradient: {
				shadeIntensity: 1,
				opacityFrom: 0.7,
				opacityTo: 0.2,
			},
		},
		colors: ["#a29bfe", "#00cec9"],
		grid: {
			show: false,
			padding: {
				left: 0,
				right: 0,
			},
		},
		xaxis: {
			categories: botActivity.categories,
			labels: {
				style: {
					colors: theme.palette.text.secondary,
				},
			},
			axisBorder: {
				show: false,
			},
			axisTicks: {
				show: false,
			},
		},
		yaxis: {
			show: false,
			labels: {
				show: false,
			},
		},
		tooltip: {
			theme: "light",
		},
		legend: {
			show: false,
		},
	};

	if (loading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<div className="container-fluid p-3">
			{/* Header */}
			<div className="row mb-4 d-flex align-items-center">
				<div className="col">
					<h4 className="fw-bold">Welcome to Bot Manager</h4>
					<p className="text-secondary">Monitor and control your Discord bots</p>
				</div>
				<div className="col-auto">
					<Button
						variant="contained"
						startIcon={<Add />}
						component={Link}
						to="/bots/new"
						sx={{
							bgcolor: "#6c5ce7",
							borderRadius: "50px",
							px: 3,
							py: 1,
							"&:hover": {
								bgcolor: "#5549c2",
							},
						}}
					>
						Add New Bot
					</Button>
				</div>
			</div>

			{/* Stats Cards */}
			<div className="row mb-4">
				<div className="col-md-3 col-sm-6 mb-3 mb-md-0">
					<Card
						elevation={0}
						sx={{
							borderRadius: 3,
							height: "100%",
							bgcolor: "#a29bfe",
							color: "white",
						}}
					>
						<CardContent>
							<Box display="flex" alignItems="center" mb={1}>
								<Box
									sx={{
										bgcolor: "rgba(255, 255, 255, 0.2)",
										borderRadius: "50%",
										width: 40,
										height: 40,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										mr: 2,
									}}
								>
									<SmartToy />
								</Box>
								<Typography variant="body2">Total Bots</Typography>
							</Box>
							<Typography variant="h1" fontWeight="bold" sx={{ fontSize: "4rem", my: 1 }}>
								{stats.total}
							</Typography>
							<Typography variant="body2" sx={{ opacity: 0.8 }}>
								+3 last week
							</Typography>
						</CardContent>
					</Card>
				</div>

				<div className="col-md-3 col-sm-6 mb-3 mb-md-0">
					<Card
						elevation={0}
						sx={{
							borderRadius: 3,
							height: "100%",
							bgcolor: "#55efc4",
							color: "white",
						}}
					>
						<CardContent>
							<Box display="flex" alignItems="center" mb={1}>
								<Box
									sx={{
										bgcolor: "rgba(255, 255, 255, 0.2)",
										borderRadius: "50%",
										width: 40,
										height: 40,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										mr: 2,
									}}
								>
									<PlayArrow />
								</Box>
								<Typography variant="body2">Running</Typography>
							</Box>
							<Typography variant="h1" fontWeight="bold" sx={{ fontSize: "4rem", my: 1 }}>
								{stats.running}
							</Typography>
							<Typography variant="body2" sx={{ opacity: 0.8 }}>
								{stats.running === 0 ? "No active bots" : `${Math.round((stats.running / stats.total) * 100)}% of total`}
							</Typography>
						</CardContent>
					</Card>
				</div>

				<div className="col-md-3 col-sm-6 mb-3 mb-md-0">
					<Card
						elevation={0}
						sx={{
							borderRadius: 3,
							height: "100%",
							bgcolor: "#b2bec3",
							color: "white",
						}}
					>
						<CardContent>
							<Box display="flex" alignItems="center" mb={1}>
								<Box
									sx={{
										bgcolor: "rgba(255, 255, 255, 0.2)",
										borderRadius: "50%",
										width: 40,
										height: 40,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										mr: 2,
									}}
								>
									<Stop />
								</Box>
								<Typography variant="body2">Stopped</Typography>
							</Box>
							<Typography variant="h1" fontWeight="bold" sx={{ fontSize: "4rem", my: 1 }}>
								{stats.stopped}
							</Typography>
							<Typography variant="body2" sx={{ opacity: 0.8 }}>
								{stats.stopped === 0 ? "All bots active" : `${Math.round((stats.stopped / stats.total) * 100)}% of total`}
							</Typography>
						</CardContent>
					</Card>
				</div>

				<div className="col-md-3 col-sm-6">
					<Card
						elevation={0}
						sx={{
							borderRadius: 3,
							height: "100%",
							bgcolor: "#ff7675",
							color: "white",
						}}
					>
						<CardContent>
							<Box display="flex" alignItems="center" mb={1}>
								<Box
									sx={{
										bgcolor: "rgba(255, 255, 255, 0.2)",
										borderRadius: "50%",
										width: 40,
										height: 40,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										mr: 2,
									}}
								>
									<Error />
								</Box>
								<Typography variant="body2">Error</Typography>
							</Box>
							<Typography variant="h1" fontWeight="bold" sx={{ fontSize: "4rem", my: 1 }}>
								{stats.error}
							</Typography>
							<Typography variant="body2" sx={{ opacity: 0.8 }}>
								{stats.error === 0 ? "All systems go" : `${Math.round((stats.error / stats.total) * 100)}% of total`}
							</Typography>
						</CardContent>
					</Card>
				</div>
			</div>

			{/* Main content - 3 column layout with equal heights */}
			<div className="row g-4">
				{/* Left column */}
				<div className="col-md-4">
					{/* Bot Activity - Height matches Discord Bot Platform + Quick Actions */}
					<Card
						elevation={0}
						sx={{
							borderRadius: 3,
							mb: 3,
							overflow: "hidden",
							bgcolor: "#FFFFFF",
						}}
					>
						<CardContent sx={{ p: 3, pb: 0 }}>
							<div className="d-flex justify-content-start mb-2">
								<Typography variant="h6" fontWeight="bold" sx={{ fontSize: "1.25rem", mr: 2 }}>
									Bot Activity
								</Typography>
								<Box
									sx={{
										display: "flex",
										bgcolor: "#f1f2f6",
										borderRadius: 4,
										padding: "4px",
									}}
								>
									<Button
										size="small"
										variant="contained"
										sx={{
											borderRadius: 4,
											bgcolor: "#6c5ce7",
											boxShadow: "none",
											mx: 0.5,
											"&:hover": {
												bgcolor: "#5549c2",
												boxShadow: "none",
											},
										}}
									>
										Week
									</Button>
									<Button size="small" variant="text" sx={{ borderRadius: 4, mx: 0.5 }}>
										Month
									</Button>
									<Button size="small" variant="text" sx={{ borderRadius: 4, mx: 0.5 }}>
										Year
									</Button>
								</Box>
							</div>
						</CardContent>
						<Box sx={{ height: 300, pb: 2 }}>
							<Chart options={chartOptions} series={botActivity.series} type="area" height="100%" width="100%" />
						</Box>
					</Card>

					{/* Recent Bots */}
					<Card
						elevation={0}
						sx={{
							borderRadius: 3,
							bgcolor: "#FFFFFF",
						}}
					>
						<CardContent sx={{ p: 3 }}>
							<Typography variant="h6" fontWeight="bold" mb={3} sx={{ fontSize: "1.25rem" }}>
								Recent Bots
							</Typography>

							{bots.length === 0 ? (
								<Box sx={{ textAlign: "center", py: 3 }}>
									<Typography color="text.secondary">No bots available. Add a bot to get started.</Typography>
								</Box>
							) : (
								bots.slice(0, 1).map((bot) => (
									<Box key={bot?.id || "no-id"}>
										{bot ? (
											<>
												<Box
													sx={{
														display: "flex",
														justifyContent: "space-between",
														alignItems: "center",
														mb: 1,
													}}
												>
													<Box sx={{ display: "flex", alignItems: "center" }}>
														<Box
															sx={{
																width: 40,
																height: 40,
																borderRadius: "12px",
																bgcolor: bot.status === "running" ? "#55efc4" : bot.status === "error" ? "#ff7675" : "#b2bec3",
																color: "white",
																display: "flex",
																alignItems: "center",
																justifyContent: "center",
																mr: 2,
															}}
														>
															<Memory />
														</Box>
														<Typography variant="body1" fontWeight="bold">
															{bot.name || "Unnamed Bot"}
														</Typography>
													</Box>
													<Box
														sx={{
															px: 1.5,
															py: 0.5,
															borderRadius: 1,
															bgcolor:
																bot.status === "running"
																	? alpha("#55efc4", 0.2)
																	: bot.status === "error"
																	? alpha("#ff7675", 0.2)
																	: alpha("#b2bec3", 0.2),
															color: bot.status === "running" ? "#00b894" : bot.status === "error" ? "#d63031" : "#636e72",
															fontSize: "0.75rem",
															fontWeight: "bold",
															textTransform: "uppercase",
														}}
													>
														{bot.status || "unknown"}
													</Box>
												</Box>
												<Typography variant="body2" color="text.secondary" mb={2}>
													Created: {new Date(bot.created_at || new Date()).toLocaleDateString()}
												</Typography>

												<div className="d-flex align-items-center mt-4">
													<div className="flex-grow-1 me-2">
														<LinearProgress
															variant="determinate"
															value={bot.status === "running" ? 100 : 0}
															sx={{
																height: 8,
																borderRadius: 4,
																bgcolor: alpha("#dfe6e9", 0.6),
																"& .MuiLinearProgress-bar": {
																	bgcolor: bot.status === "running" ? "#55efc4" : bot.status === "error" ? "#ff7675" : "#b2bec3",
																},
															}}
														/>
													</div>
												</div>
												<div className="text-end mt-3">
													<Button
														variant="outlined"
														size="small"
														component={Link}
														to={`/bots/${bot.id}`}
														sx={{
															borderColor: "#6c5ce7",
															color: "#6c5ce7",
															borderRadius: "50px",
															px: 3,
															"&:hover": {
																borderColor: "#5549c2",
																bgcolor: alpha("#6c5ce7", 0.1),
															},
														}}
													>
														View Details
													</Button>
												</div>
											</>
										) : (
											<Typography color="text.secondary">Bot data not available</Typography>
										)}
									</Box>
								))
							)}
						</CardContent>
					</Card>
				</div>

				{/* Middle column */}
				<div className="col-md-4">
					{/* Discord Bot Platform */}
					<Card
						elevation={0}
						sx={{
							borderRadius: 3,
							mb: 3,
							position: "relative",
							overflow: "hidden",
							bgcolor: "#6c5ce7",
							height: "212px", // Fixed height to match screenshot
						}}
					>
						<Box
							sx={{
								p: 4,
								display: "flex",
								flexDirection: "column",
								justifyContent: "center",
								alignItems: "center",
								color: "white",
								textAlign: "center",
								height: "100%",
							}}
						>
							<Typography variant="h4" fontWeight="bold" gutterBottom>
								Discord Bot Platform
							</Typography>
							<Box
								sx={{
									bgcolor: "#ffd32a",
									width: 60,
									height: 60,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									mt: 2,
									borderRadius: 1,
									fontWeight: "bold",
									marginBottom: 2,
									fontSize: "24px",
									filter: "drop-shadow(0 0 8px rgba(255,255,255,0.3))",
								}}
							>
								JS
							</Box>
							<Typography variant="subtitle1" sx={{ mb: 2 }}>
								Manage all your Discord bots in one place
							</Typography>
						</Box>
					</Card>

					{/* Quick Actions */}
					<Card
						elevation={0}
						sx={{
							borderRadius: 3,
							mb: 3,
							bgcolor: "#FFFFFF",
						}}
					>
						<CardContent sx={{ p: 3 }}>
							<Typography variant="h6" fontWeight="bold" mb={3} sx={{ fontSize: "1.25rem" }}>
								Quick Actions
							</Typography>
							<Button
								variant="contained"
								component={Link}
								to="/bots/new"
								fullWidth
								startIcon={<Add />}
								size="large"
								sx={{
									bgcolor: "#6c5ce7",
									borderRadius: "50px",
									py: 1.5,
									"&:hover": {
										bgcolor: "#5549c2",
									},
								}}
							>
								Add New Bot
							</Button>
						</CardContent>
					</Card>

					{/* Bot Types */}
					<Card
						elevation={0}
						sx={{
							borderRadius: 3,
							bgcolor: "#FFFFFF",
						}}
					>
						<CardContent sx={{ p: 3 }}>
							<Typography variant="h6" fontWeight="bold" mb={3} sx={{ fontSize: "1.25rem" }}>
								Bot Types
							</Typography>

							{botTypes.map((type) => (
								<Box key={type.name} sx={{ mb: 3 }}>
									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											mb: 1,
										}}
									>
										<Typography variant="body2">{type.name}</Typography>
										<Typography variant="body2" fontWeight="bold">
											{type.percent}%
										</Typography>
									</Box>
									<LinearProgress
										variant="determinate"
										value={type.percent}
										sx={{
											height: 8,
											borderRadius: 4,
											bgcolor: alpha(type.color, 0.1),
											"& .MuiLinearProgress-bar": {
												bgcolor: type.color,
											},
										}}
									/>
								</Box>
							))}
						</CardContent>
					</Card>
				</div>

				{/* Right column - Activity Log */}
				<div className="col-md-4">
					<Card
						elevation={0}
						sx={{
							borderRadius: 3,
							bgcolor: "#FFFFFF",
							height: "100%",
							display: "flex",
							flexDirection: "column",
						}}
					>
						<CardContent sx={{ p: 3, pb: 1 }}>
							<Typography variant="h6" fontWeight="bold" mb={2} sx={{ fontSize: "1.25rem" }}>
								Activity Log
							</Typography>
						</CardContent>

						<Box
							sx={{
								flexGrow: 1,
								overflow: "auto",
								px: 3,
								pb: 2,
								maxHeight: "calc(8 * 70px)", // Show 8 items without scroll
							}}
						>
							{activities.length === 0 ? (
								<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 3 }}>
									<Typography color="text.secondary">No recent activities</Typography>
								</Box>
							) : (
								activities.slice(0, 9).map((activity, index) => (
									<Box
										key={index}
										sx={{
											py: 2,
											borderBottom: index < 8 ? "1px solid rgba(0,0,0,0.08)" : "none",
										}}
									>
										<Box sx={{ display: "flex", alignItems: "center" }}>
											<Avatar
												sx={{
													width: 36,
													height: 36,
													bgcolor: activity.action.includes("start") ? "#55efc4" : activity.action.includes("stop") ? "#b2bec3" : "#6c5ce7",
													mr: 2,
												}}
											>
												{activity.action.includes("start") && <PlayArrow fontSize="small" />}
												{activity.action.includes("stop") && <Stop fontSize="small" />}
												{activity.action.includes("created") && <Add fontSize="small" />}
											</Avatar>

											<Box>
												<Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
													<Typography variant="body2" fontWeight="medium" color="text.primary" sx={{ mr: 0.5 }}>
														{activity.user?.username || "Unknown"}
													</Typography>
													<Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
														{activity.action || "performed action on"}
													</Typography>
													{activity.bot && (
														<Typography
															variant="body2"
															component={Link}
															to={`/bots/${activity.bot.id}`}
															sx={{
																textDecoration: "none",
																color: "#6c5ce7",
																fontWeight: "500",
																"&:hover": {
																	textDecoration: "underline",
																},
															}}
														>
															{activity.bot.name || "Unnamed Bot"}
														</Typography>
													)}
												</Box>

												<Typography variant="caption" color="text.secondary">
													{new Date(activity.timestamp || new Date()).toLocaleDateString("en-US", {
														month: "short",
														day: "numeric",
														year: "numeric",
													})}{" "}
													at{" "}
													{new Date(activity.timestamp || new Date()).toLocaleTimeString("en-US", {
														hour: "2-digit",
														minute: "2-digit",
														hour12: false,
													})}
												</Typography>
											</Box>
										</Box>
									</Box>
								))
							)}
						</Box>
					</Card>
				</div>
			</div>
		</div>
	);
}

export default Dashboard;
